import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import HarvestFormDialog from "@/components/HarvestFormDialog";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Pencil, Trash2, Calendar as CalIcon, AlertTriangle, CheckCircle2,
  ChevronLeft, ChevronRight, List as ListIcon, LayoutGrid, Droplets,
} from "lucide-react";
import { toast } from "sonner";
import {
  format, parseISO, isPast, startOfWeek, endOfWeek, addDays,
  isSameDay, startOfDay, startOfMonth, endOfMonth, addWeeks, subWeeks,
  addMonths, subMonths, eachDayOfInterval, isSameMonth, isToday,
} from "date-fns";

const TYPE_COLORS = {
  inspection: "bg-amber-100 text-amber-900",
  treatment: "bg-red-100 text-red-800",
  feeding: "bg-emerald-100 text-emerald-800",
  harvest: "bg-yellow-200 text-amber-900",
  other: "bg-stone-100 text-stone-700",
};

const TYPE_DOT = {
  inspection: "bg-amber-500",
  treatment: "bg-red-500",
  feeding: "bg-emerald-500",
  harvest: "bg-yellow-600",
  other: "bg-stone-400",
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [hives, setHives] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [harvestOpen, setHarvestOpen] = useState(false);
  const [harvestFromEvent, setHarvestFromEvent] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [e, h] = await Promise.all([api.get("/events"), api.get("/hives")]);
    setEvents(e.data);
    setHives(h.data);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (ev) => {
    await api.patch(`/events/${ev.id}`, { completed: !ev.completed });
    load();
  };

  const del = async (ev) => {
    await api.delete(`/events/${ev.id}`);
    load();
  };

  const logHarvest = (ev) => {
    setHarvestFromEvent(ev);
    setHarvestOpen(true);
  };

  const editEvent = (ev) => setEditing(ev);

  const overdue = events.filter((e) => !e.completed && isPast(parseISO(e.due_date)));
  const upcoming = events.filter((e) => !e.completed && !isPast(parseISO(e.due_date)));
  const done = events.filter((e) => e.completed);

  return (
    <div className="space-y-8" data-testid="events-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Schedule</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Reminders</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="add-event-button">
              <Plus className="w-4 h-4 mr-2" /> New reminder
            </Button>
          </DialogTrigger>
          <EventFormDialog hives={hives} onSaved={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      <Tabs value={view} onValueChange={setView} data-testid="events-view-tabs">
        <TabsList className="bg-stone-100 rounded-full p-1">
          <TabsTrigger value="list" className="rounded-full" data-testid="tab-list">
            <ListIcon className="w-4 h-4 mr-1.5" /> List
          </TabsTrigger>
          <TabsTrigger value="week" className="rounded-full" data-testid="tab-week">
            <CalIcon className="w-4 h-4 mr-1.5" /> Week
          </TabsTrigger>
          <TabsTrigger value="month" className="rounded-full" data-testid="tab-month">
            <LayoutGrid className="w-4 h-4 mr-1.5" /> Month
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6 space-y-8">
          {overdue.length > 0 && (
            <Section title="Overdue" icon={<AlertTriangle className="w-4 h-4 text-orange-600" />}
              events={overdue} hives={hives} onToggle={toggle} onDelete={del} onEdit={editEvent} onLogHarvest={logHarvest} testid="overdue-list" />
          )}
          <Section title="Upcoming" icon={<CalIcon className="w-4 h-4 text-amber-700" />}
            events={upcoming} hives={hives} onToggle={toggle} onDelete={del} onEdit={editEvent} onLogHarvest={logHarvest} testid="upcoming-list" />
          {done.length > 0 && (
            <Section title="Completed" icon={<CheckCircle2 className="w-4 h-4 text-emerald-700" />}
              events={done} hives={hives} onToggle={toggle} onDelete={del} onEdit={editEvent} testid="completed-list" muted />
          )}
          {events.length === 0 && (
            <div className="bg-white border border-stone-200 rounded-3xl p-12 text-center text-stone-500">
              No reminders yet. Schedule your next inspection or treatment.
            </div>
          )}
        </TabsContent>

        <TabsContent value="week" className="mt-6">
          <WeekView events={events} hives={hives} onToggle={toggle} onDelete={del} onEdit={editEvent} />
        </TabsContent>

        <TabsContent value="month" className="mt-6">
          <MonthView events={events} hives={hives} onToggle={toggle} onDelete={del} onEdit={editEvent} />
        </TabsContent>
      </Tabs>

      <Dialog open={harvestOpen} onOpenChange={(o) => { setHarvestOpen(o); if (!o) setHarvestFromEvent(null); }}>
        {harvestOpen && (
          <HarvestFormDialog
            key={harvestFromEvent?.id || "new"}
            hives={hives}
            event={harvestFromEvent}
            onSaved={() => { setHarvestOpen(false); setHarvestFromEvent(null); load(); }}
          />
        )}
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        {editing && (
          <EventFormDialog
            key={editing.id}
            hives={hives}
            event={editing}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </Dialog>
    </div>
  );
}

function WeekView({ events, hives, onToggle, onDelete, onEdit }) {
  const [anchor, setAnchor] = useState(new Date());
  const start = startOfWeek(anchor, { weekStartsOn: 1 });
  const end = endOfWeek(anchor, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });

  const byDay = useMemo(() => {
    const map = new Map();
    days.forEach((d) => map.set(d.toDateString(), []));
    events.forEach((e) => {
      const d = parseISO(e.due_date);
      const key = startOfDay(d).toDateString();
      if (map.has(key)) map.get(key).push(e);
    });
    for (const arr of map.values()) arr.sort((a, b) => a.due_date.localeCompare(b.due_date));
    return map;
  }, [events, start.toISOString()]);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-6" data-testid="week-view">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-lg">
          {format(start, "MMM d")} — {format(end, "MMM d, yyyy")}
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => setAnchor(subWeeks(anchor, 1))} className="rounded-full" data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} className="rounded-full px-3" data-testid="week-today">Today</Button>
          <Button variant="ghost" size="icon" onClick={() => setAnchor(addWeeks(anchor, 1))} className="rounded-full" data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d) => {
          const list = byDay.get(d.toDateString()) || [];
          const today = isToday(d);
          return (
            <div key={d.toISOString()} className={`border rounded-2xl p-3 min-h-[140px] ${today ? "border-yellow-400 bg-yellow-50/40" : "border-stone-200"}`} data-testid={`day-${format(d, "yyyy-MM-dd")}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{format(d, "EEE")}</div>
                <div className={`font-display text-lg ${today ? "text-amber-800 font-medium" : ""}`}>{format(d, "d")}</div>
              </div>
              <div className="space-y-1.5">
                {list.length === 0 ? (
                  <div className="text-xs text-stone-300">—</div>
                ) : list.map((e) => (
                  <MiniEvent key={e.id} ev={e} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} hives={hives} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ events, hives, onToggle, onDelete, onEdit }) {
  const [anchor, setAnchor] = useState(new Date());
  const monthStart = startOfMonth(anchor);
  const monthEnd = endOfMonth(anchor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const [selected, setSelected] = useState(null);

  const byDay = useMemo(() => {
    const map = new Map();
    events.forEach((e) => {
      const key = startOfDay(parseISO(e.due_date)).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  }, [events]);

  const selectedEvents = selected ? (byDay.get(selected.toDateString()) || []) : [];

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-4 sm:p-6" data-testid="month-view">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl">{format(anchor, "MMMM yyyy")}</div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => setAnchor(subMonths(anchor, 1))} className="rounded-full" data-testid="month-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setAnchor(new Date()); setSelected(new Date()); }} className="rounded-full px-3" data-testid="month-today">Today</Button>
            <Button variant="ghost" size="icon" onClick={() => setAnchor(addMonths(anchor, 1))} className="rounded-full" data-testid="month-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs text-stone-500 mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-center uppercase tracking-[0.15em]">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const list = byDay.get(d.toDateString()) || [];
            const isCurrentMonth = isSameMonth(d, anchor);
            const isSel = selected && isSameDay(d, selected);
            const today = isToday(d);
            return (
              <button
                key={d.toISOString()}
                onClick={() => setSelected(d)}
                className={`aspect-square rounded-xl p-1.5 text-left flex flex-col transition-colors
                  ${isSel ? "bg-yellow-400 text-amber-900" : today ? "bg-yellow-50 border border-yellow-200" : "hover:bg-stone-50"}
                  ${!isCurrentMonth ? "text-stone-300" : ""}`}
                data-testid={`month-day-${format(d, "yyyy-MM-dd")}`}
              >
                <span className={`text-sm ${today && !isSel ? "font-semibold text-amber-800" : ""}`}>{format(d, "d")}</span>
                <div className="mt-auto flex flex-wrap gap-0.5">
                  {list.slice(0, 4).map((e) => (
                    <span key={e.id} className={`w-1.5 h-1.5 rounded-full ${TYPE_DOT[e.event_type] || TYPE_DOT.other}`} />
                  ))}
                  {list.length > 4 && <span className="text-[10px] text-stone-500">+{list.length - 4}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="month-selected-panel">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-1">
          {selected ? "Reminders on" : "Pick a day"}
        </div>
        {selected && (
          <div className="font-display text-xl font-medium mb-4">{format(selected, "EEEE, MMM d")}</div>
        )}
        {!selected && <div className="text-sm text-stone-500">Click any day on the calendar to view reminders.</div>}
        {selected && selectedEvents.length === 0 && (
          <div className="text-sm text-stone-500">No reminders.</div>
        )}
        <div className="space-y-2">
          {selectedEvents.map((e) => (
            <MiniEvent key={e.id} ev={e} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} hives={hives} expanded />
          ))}
        </div>
      </div>
    </div>
  );
}

function MiniEvent({ ev, onToggle, onDelete, onEdit, hives, expanded }) {
  const hiveName = hives.find((h) => h.id === ev.hive_id)?.name;
  return (
    <div className={`group rounded-xl px-2 py-1.5 text-xs flex items-start gap-2 ${ev.completed ? "opacity-50" : ""} ${expanded ? "bg-stone-50 border border-stone-200 p-3" : "bg-stone-50 hover:bg-stone-100"}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${TYPE_DOT[ev.event_type] || TYPE_DOT.other}`} />
      <div className="flex-1 min-w-0">
        <div className={`truncate font-medium ${ev.completed ? "line-through" : ""} ${expanded ? "text-sm" : ""}`}>{ev.title}</div>
        <div className={`text-stone-500 ${expanded ? "text-xs mt-0.5" : "text-[10px]"}`}>
          {format(parseISO(ev.due_date), "h:mm a")}{hiveName ? ` · ${hiveName}` : ""}
        </div>
        {expanded && ev.notes && <div className="text-xs text-stone-600 mt-1">{ev.notes}</div>}
      </div>
      {expanded && (
        <div className="flex gap-1">
          <button onClick={() => onToggle(ev)} className="text-stone-400 hover:text-emerald-600" data-testid={`mini-toggle-${ev.id}`}>
            <CheckCircle2 className="w-4 h-4" />
          </button>
          {onEdit && (
            <button onClick={() => onEdit(ev)} className="text-stone-400 hover:text-stone-700" data-testid={`mini-edit-${ev.id}`}>
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onDelete(ev)} className="text-stone-400 hover:text-red-600" data-testid={`mini-delete-${ev.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, events, hives, onToggle, onDelete, onEdit, onLogHarvest, muted, testid }) {
  const hiveName = (id) => hives.find((h) => h.id === id)?.name;
  return (
    <div className={muted ? "opacity-80" : ""} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="font-display text-xl font-medium">{title}</h2>
        <span className="text-sm text-stone-500">({events.length})</span>
      </div>
      {events.length === 0 ? (
        <div className="text-sm text-stone-500">Nothing here.</div>
      ) : (
        <ul className="space-y-2">
          {events.map((ev) => (
            <li key={ev.id} className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-4" data-testid={`event-${ev.id}`}>
              <Checkbox checked={ev.completed} onCheckedChange={() => onToggle(ev)} data-testid={`event-toggle-${ev.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium ${ev.completed ? "line-through text-stone-400" : ""}`}>{ev.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_COLORS[ev.event_type] || TYPE_COLORS.other}`}>{ev.event_type}</span>
                  {ev.hive_id && <span className="text-xs text-stone-500">· {hiveName(ev.hive_id) || "hive"}</span>}
                </div>
                <div className="text-xs text-stone-500 mt-1">{format(parseISO(ev.due_date), "EEE, MMM d, yyyy · h:mm a")}</div>
                {ev.notes && <div className="text-sm text-stone-600 mt-1">{ev.notes}</div>}
              </div>
              {ev.event_type === "harvest" && !ev.completed && onLogHarvest && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100"
                  onClick={() => onLogHarvest(ev)}
                  data-testid={`event-log-harvest-${ev.id}`}
                >
                  <Droplets className="w-3.5 h-3.5 mr-1.5" /> Log harvest
                </Button>
              )}
              {onEdit && (
                <button onClick={() => onEdit(ev)} className="text-stone-400 hover:text-stone-700" title="Edit" data-testid={`event-edit-${ev.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => onDelete(ev)} className="text-stone-400 hover:text-red-600" title="Delete" data-testid={`event-delete-${ev.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EventFormDialog({ hives, event, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!event;
  const defaultDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(10, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  };
  // A stored due_date is UTC ISO; convert to the datetime-local (local-time) value.
  const toLocalInput = (iso) => {
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const [form, setForm] = useState({
    title: event?.title || "",
    event_type: event?.event_type || "inspection",
    hive_id: event ? (event.hive_id || "") : (user?.default_hive_id && hives.find((h) => h.id === user.default_hive_id) ? user.default_hive_id : ""),
    due_date: event?.due_date ? toLocalInput(event.due_date) : defaultDate(),
    notes: event?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        hive_id: form.hive_id || null,
        due_date: new Date(form.due_date).toISOString(),
      };
      if (isEdit) {
        await api.patch(`/events/${event.id}`, payload);
        toast.success("Reminder updated");
      } else {
        await api.post("/events", payload);
        toast.success("Reminder added");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium">{isEdit ? "Edit reminder" : "New reminder"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Title</Label>
          <Input value={form.title} maxLength={200} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Varroa mite check" data-testid="event-title-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
              <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="treatment">Treatment</SelectItem>
                <SelectItem value="feeding">Feeding</SelectItem>
                <SelectItem value="harvest">Harvest</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Hive (optional)</Label>
            <Select value={form.hive_id || "none"} onValueChange={(v) => setForm({ ...form, hive_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="event-hive-select"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {hives.map((h) => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>When</Label>
          <Input type="datetime-local" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="event-date-input" />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea rows={2} maxLength={10000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="event-notes-input" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving || !form.title} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="event-save-button">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Save"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
